import { useEffect, useRef, useState } from 'react';
import { prefersReducedMotion } from '../components/surface/tokens';

/**
 * Swipe a full-screen viewer down to close it — the portal, a video, an album, a photo.
 *
 * The picture follows the finger and shrinks a little as it goes, so the gesture is visible
 * the whole way instead of being a threshold you discover on release. Let go past
 * `distance`, or flick faster than `velocity`, and it closes; anything less springs back.
 *
 * WHY A REF AND NOT onTouchMove: React attaches touchstart / touchmove at the root as
 * PASSIVE listeners, so `preventDefault()` inside a JSX handler is silently ignored and iOS
 * keeps the gesture for itself. These are bound to the element, non-passive. Binding to the
 * element also means they run BEFORE the host's own React handlers — which is what lets
 * StoryViewer ask `claimed.current` on touchend and leave a dismiss alone instead of also
 * reading it as a tap or a page turn.
 *
 * The axis is decided once, after `slop` pixels: a drag that starts sideways or upward is
 * the host's (next person, the activity sheet) and is never stolen. Touches that start on a
 * control, on `[data-no-swipe]`, or with a second finger down (a pinch) are left alone too —
 * unless the control is marked `[data-swipe-ok]`, which is how a viewer whose whole stage is
 * made of tap-zone buttons still gets the gesture.
 *
 * Under prefers-reduced-motion nothing is dragged — the threshold still closes it, with no
 * travel.
 */
const CONTROLS = 'button, a, input, textarea, select, [data-no-swipe]';
/** The drag is read as a fraction of this, for the shrink and the fade. */
const TRAVEL = 260;

export default function useSwipeToDismiss(ref, {
  onDismiss,
  enabled = true,
  distance = 110,
  velocity = 0.5, // px per ms
  slop = 10,
} = {}) {
  const [dy, setDy] = useState(0);
  const [dragging, setDragging] = useState(false);
  const claimed = useRef(false);
  const gesture = useRef(null);
  const cb = useRef(onDismiss);
  cb.current = onDismiss;
  const reduced = prefersReducedMotion();

  useEffect(() => {
    const el = ref?.current;
    if (!el || !enabled) return undefined;

    const onStart = (e) => {
      claimed.current = false;
      gesture.current = null;
      if (e.touches.length !== 1) return;
      if (e.target?.closest?.(CONTROLS) && !e.target.closest('[data-swipe-ok]')) return;
      const t = e.touches[0];
      gesture.current = { x: t.clientX, y: t.clientY, at: e.timeStamp || Date.now(), axis: null };
    };

    const onMove = (e) => {
      const g = gesture.current;
      if (!g) return;
      // A second finger means a pinch — hand the gesture back untouched.
      if (e.touches.length !== 1) { gesture.current = null; setDragging(false); setDy(0); return; }
      const t = e.touches[0];
      const dx = t.clientX - g.x;
      const d = t.clientY - g.y;
      if (g.axis === null) {
        if (Math.abs(dx) < slop && Math.abs(d) < slop) return;
        g.axis = d > 0 && Math.abs(d) > Math.abs(dx) ? 'dismiss' : 'host';
        if (g.axis === 'dismiss') { claimed.current = true; if (!reduced) setDragging(true); }
      }
      if (g.axis !== 'dismiss') return;
      if (e.cancelable) e.preventDefault();
      if (!reduced) setDy(Math.max(0, d));
    };

    const onEnd = (e) => {
      const g = gesture.current;
      gesture.current = null;
      setDragging(false);
      if (!g || g.axis !== 'dismiss') return;
      const t = e.changedTouches[0];
      const d = Math.max(0, t.clientY - g.y);
      const ms = Math.max(1, (e.timeStamp || Date.now()) - g.at);
      if (d > distance || d / ms > velocity) { cb.current?.(); return; }
      setDy(0); // springs back on the transition below
    };

    const onCancel = () => { gesture.current = null; setDragging(false); setDy(0); };

    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd, { passive: true });
    el.addEventListener('touchcancel', onCancel, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onCancel);
    };
  }, [ref, enabled, distance, velocity, slop, reduced]);

  const progress = Math.min(1, dy / TRAVEL);
  return {
    dy,
    dragging,
    progress,
    /** True while the CURRENT gesture is a dismiss — hosts read it to skip their tap logic. */
    claimed,
    /** Drop onto the element the ref is on. */
    style: dy
      ? {
        transform: `translateY(${Math.round(dy)}px) scale(${(1 - progress * 0.08).toFixed(4)})`,
        transformOrigin: '50% 0%',
        borderRadius: Math.round(progress * 28),
        overflow: 'hidden',
        willChange: 'transform',
        transition: dragging ? 'none' : 'transform 260ms cubic-bezier(.22,1,.36,1), border-radius 260ms linear',
      }
      : { transition: dragging ? 'none' : 'transform 260ms cubic-bezier(.22,1,.36,1)' },
  };
}
